import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient, gql } from '@apollo/client';
import { OrganizationMember, useGetMembersQuery } from 'gql/graphql';
import { transformDataForTable } from '../../components/table/membersTableHelpers';
import { IMembersTableProps } from '../../components/table/MembersTable';

export const GET_REMOVED_MEMBER_ID = gql`
  query GetRemovedMemberId {
    removedMemberId @client
  }
`;

export function useDebounce(value: string) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 400);
    return () => {
      clearTimeout(handler);
    };
  }, [value]);
  return debouncedValue;
}

export const useMembersData = (props: IMembersTableProps) => {
  const history = useHistory();
  const client = useApolloClient();

  // Get removed member ID from Apollo cache
  const removedMemberData = client.readQuery({
    query: GET_REMOVED_MEMBER_ID,
  });

  let removedMemberId = removedMemberData?.removedMemberId;
  if (removedMemberId) {
    removedMemberId = removedMemberId.replace('OrganizationMember:', '');
  }

  // Pagination state
  const [searchQueryPagingConfig, setSearchQueryPagingConfig] = useState({
    page: parseInt(props.match.params.page ?? 1),
    pageSize: 10,
  });

  const simpleQueryPagingConfig = {
    page: parseInt(props.match.params.page ?? 1),
    pageSize: 10,
  };

  // Search state
  const [searchFilter, setSearchFilter] = useState('');
  const debouncedSearchFilter = useDebounce(searchFilter);
  const [searchStringIsValid, setSearchStringIsValid] = useState(true);
  const [resetPagination, setResetPagination] = useState(false);

  const { page, pageSize } = debouncedSearchFilter
    ? searchQueryPagingConfig
    : simpleQueryPagingConfig;

  // Fetch policy based on navigation state
  const state = history.location.state as { from?: string };
  const fetchPolicy =
    state && state.from === 'member-details'
      ? 'cache-first'
      : 'cache-and-network';

  // GraphQL query
  const { data: membersData, loading: membersLoading } = useGetMembersQuery({
    variables: {
      skip: pageSize * (page - 1),
      take: pageSize,
      query: searchStringIsValid ? debouncedSearchFilter : '',
    },
    fetchPolicy,
  });

  // Pagination reset effects
  useEffect(() => {
    if (debouncedSearchFilter) {
      setResetPagination(true);
    }
  }, [debouncedSearchFilter]);

  useEffect(() => {
    if (resetPagination) {
      setSearchQueryPagingConfig((prevConfig) => ({
        ...prevConfig,
        page: 1,
      }));
      setResetPagination(false);
    }
  }, [resetPagination]);

  useEffect(() => {
    if (debouncedSearchFilter) {
      const newPage = props.match.params.page
        ? parseInt(props.match.params.page)
        : 1;
      setSearchQueryPagingConfig((prevConfig) => ({
        ...prevConfig,
        page: newPage,
      }));
    }
  }, [debouncedSearchFilter, props.match.params.page]);

  // Data processing
  const { items, totalCount } = membersData?.organization?.members || {
    items: [],
    totalCount: 0,
  };

  const cachedFilteredItems = items?.filter(
    (member) => member.id !== removedMemberId,
  );

  const memberToShow = useMemo(
    () => transformDataForTable(cachedFilteredItems as OrganizationMember[]),
    [cachedFilteredItems]
  );

  const pagination = totalCount < pageSize ? 'hidden' : 'complex';

  return {
    // Data
    memberToShow,
    totalCount,
    membersLoading,
    
    // Search state
    searchFilter,
    setSearchFilter,
    searchStringIsValid,
    setSearchStringIsValid,
    
    // Pagination state
    page,
    pageSize,
    pagination,
    setResetPagination,
    
    // History
    history,
  };
};
